import { useEffect, useState } from "react";
import teamAPI from "../../../services/teamApi";
import authAPI from "../../../services/authApi";
import { TEAM_ROLE_LABELS } from "./teamConstants";
import aa from "../AdminPortal/adminApp.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";

function statusText(member) {
  if (member.trainer_active === false || member.is_blocked) {
    return "Деактивирован";
  }
  return "Активен";
}

function isDeactivated(member) {
  if (!member) return false;
  if (member.trainer_active === false) return true;
  return !!member.is_blocked;
}

/**
 * Единый блок доступа: роль, пароль, деактивация / возврат / удаление.
 */
export default function TeamMemberManagePanel({
  member,
  members = [],
  viewerIsOwner,
  disabled,
  onChanged,
  showPassword = true,
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState(member?.role || "trainer");
  const [newPassword, setNewPassword] = useState("");
  const [preview, setPreview] = useState(null);
  const [replacementId, setReplacementId] = useState("");
  const [confirmMode, setConfirmMode] = useState(null);

  const canChangeRole =
    viewerIsOwner &&
    member &&
    !member.is_owner &&
    (member.role === "admin" || member.role === "trainer");

  const deactivated = isDeactivated(member);
  const hasTrainerProfile = Boolean(member?.trainer_id);
  const canDeactivate =
    !deactivated && (member?.role === "trainer" || member?.role === "admin");

  useEffect(() => {
    setRole(member?.role || "trainer");
    setConfirmMode(null);
    setReplacementId("");
    setNewPassword("");
  }, [member?.id, member?.role, member?.is_blocked, member?.trainer_active]);

  useEffect(() => {
    if (!member?.id || !confirmMode) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    teamAPI
      .getOffboardPreview(member.id)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setReplacementId(data.replacements?.[0]?.id || "");
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [member?.id, confirmMode]);

  if (!member || member.is_owner) return null;

  const onSaveRole = async () => {
    if (role === member.role) return;
    const label = TEAM_ROLE_LABELS[role] || role;
    if (
      !window.confirm(
        `Сделать «${member.full_name || "сотрудника"}» — ${label}?`,
      )
    ) {
      setRole(member.role);
      return;
    }
    setBusy(true);
    try {
      const res = await teamAPI.updateMember(member.id, { role });
      if (res?.result !== 0) {
        toast.error(res?.error || "Ошибка");
        setRole(member.role);
        return;
      }
      toast.success("Роль обновлена");
      onChanged?.(res.member || null);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
      setRole(member.role);
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    setBusy(true);
    try {
      const res = await teamAPI.resendInvite(member.id);
      if (res?.result !== 0) {
        toast.error(res?.error || "Ошибка");
        return;
      }
      if (res.invite_url && !res.email_sent) {
        try {
          await navigator.clipboard.writeText(res.invite_url);
          toast.success("Ссылка скопирована", 6000);
        } catch {
          toast.success(res.message || "Готово");
        }
      } else {
        toast.success(res.message || "Приглашение отправлено");
      }
      onChanged?.(member);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  const onSetPassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Пароль от 8 символов");
      return;
    }
    setBusy(true);
    try {
      await authAPI.adminSetUserPassword(member.id, newPassword);
      setNewPassword("");
      toast.success("Пароль обновлён");
      onChanged?.(member);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async () => {
    if (!window.confirm(`Вернуть «${member.full_name || "сотрудника"}» в работу?`)) {
      return;
    }
    setBusy(true);
    try {
      const res = await teamAPI.reactivate(member.id);
      if (res?.result !== 0) {
        toast.error(res?.error || "Ошибка");
        return;
      }
      toast.success(res.message || "Сотрудник снова активен");
      onChanged?.(res.member || null);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  const runOffboard = async (mode) => {
    if (mode === "delete") {
      const ok = window.confirm(
        `Удалить «${member.full_name || "сотрудника"}» из команды?\n\nСтатистика сохранится. Вернуть нельзя — только новое приглашение.`,
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const payload = { mode };
      if (replacementId) payload.replacement_trainer_id = replacementId;
      const res = await teamAPI.offboard(member.id, payload);
      if (res?.result !== 0) {
        toast.error(res?.error || "Ошибка");
        return;
      }
      toast.success(res.message || "Готово");
      setConfirmMode(null);
      onChanged?.(
        mode === "delete"
          ? null
          : res.member || {
              ...member,
              trainer_active: hasTrainerProfile ? false : member.trainer_active,
              is_blocked: true,
            },
      );
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  const showReplacementPicker =
    (preview?.trainer_id || hasTrainerProfile) &&
    (preview?.replacements?.length > 0 ||
      members.some(
        (m) =>
          m.trainer_id &&
          m.trainer_active !== false &&
          m.id !== member.id,
      ));

  const replacementOptions =
    preview?.replacements?.length > 0
      ? preview.replacements
      : members
          .filter(
            (m) =>
              m.trainer_id &&
              m.trainer_active !== false &&
              m.id !== member.id,
          )
          .map((m) => ({ id: m.trainer_id, full_name: m.full_name }));

  const locked = busy || disabled;

  const deactivateHint = hasTrainerProfile
    ? "Деактивировать сотрудника, закрыть вход и перенести будущие занятия? Потом можно вернуть."
    : "Деактивировать сотрудника и закрыть вход? Потом можно вернуть.";

  const restoreHint = hasTrainerProfile
    ? "Сотрудник деактивирован. Вход закрыт, занятия перенесены."
    : "Сотрудник деактивирован. Вход в систему закрыт.";

  return (
    <div className={aa.teamAccessCard}>
      <div className={aa.teamAccessStatusRow}>
        <span className={aa.teamAccessStatusLabel}>Статус</span>
        <strong
          className={
            deactivated
              ? aa.teamAccessStatusDanger
              : aa.teamAccessStatusValue
          }
        >
          {statusText(member)}
        </strong>
      </div>

      {canChangeRole ? (
        <Field label="Роль" className={aa.teamAccessField}>
          <div className={aa.teamAccessRoleRow}>
            <Select
              value={role}
              disabled={locked || deactivated}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="trainer">Тренер</option>
              <option value="admin">Администратор</option>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={locked || deactivated || role === member.role}
              onClick={onSaveRole}
            >
              Сохранить
            </Button>
          </div>
        </Field>
      ) : (
        <div className={aa.teamAccessStatusRow}>
          <span className={aa.teamAccessStatusLabel}>Роль</span>
          <span className={aa.teamAccessStatusValue}>
            {TEAM_ROLE_LABELS[member.role] || member.role}
          </span>
        </div>
      )}

      {showPassword && !deactivated ? (
        <Field label="Новый пароль" hint="От 8 символов" className={aa.teamAccessField}>
          <div className={aa.teamAccessRoleRow}>
            <Input
              type="password"
              value={newPassword}
              disabled={locked}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Минимум 8 символов"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={locked || newPassword.length < 8}
              onClick={onSetPassword}
            >
              Задать
            </Button>
          </div>
        </Field>
      ) : null}

      {!deactivated ? (
        <button
          type="button"
          className={aa.teamAccessTextBtn}
          disabled={locked}
          onClick={onResend}
        >
          Отправить приглашение на email
        </button>
      ) : null}

      {deactivated && !confirmMode ? (
        <div className={aa.teamAccessRestoreBox}>
          <p>{restoreHint}</p>
          <Button type="button" disabled={locked} onClick={onRestore}>
            {hasTrainerProfile ? "Вернуть в работу" : "Активировать"}
          </Button>
        </div>
      ) : null}

      {confirmMode === "deactivate" ? (
        <div className={aa.teamAccessDangerBox}>
          <p>{deactivateHint}</p>
          {preview?.upcoming_sessions > 0 ? (
            <p className={aa.hubHint}>
              Занятий впереди: {preview.upcoming_sessions}
            </p>
          ) : null}
          {showReplacementPicker ? (
            <Field label="Кто ведёт занятия" className={aa.teamAccessField}>
              <Select
                value={replacementId}
                disabled={locked}
                onChange={(e) => setReplacementId(e.target.value)}
              >
                {replacementOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.full_name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <div className={aa.teamAccessDangerActions}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={locked}
              onClick={() => setConfirmMode(null)}
            >
              Отмена
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={locked}
              onClick={() => runOffboard("deactivate")}
            >
              Деактивировать
            </Button>
          </div>
        </div>
      ) : null}

      {confirmMode === "delete" ? (
        <div className={aa.teamAccessDangerBox}>
          <p>
            Удалить из команды безвозвратно? Статистика прошлых занятий
            останется.
          </p>
          {showReplacementPicker ? (
            <Field label="Кто ведёт занятия" className={aa.teamAccessField}>
              <Select
                value={replacementId}
                disabled={locked}
                onChange={(e) => setReplacementId(e.target.value)}
              >
                {replacementOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.full_name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <div className={aa.teamAccessDangerActions}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={locked}
              onClick={() => setConfirmMode(null)}
            >
              Отмена
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={locked}
              onClick={() => runOffboard("delete")}
            >
              Удалить
            </Button>
          </div>
        </div>
      ) : null}

      {!confirmMode ? (
        <div className={aa.teamAccessFooter}>
          {canDeactivate ? (
            <button
              type="button"
              className={aa.teamAccessQuietBtn}
              disabled={locked}
              onClick={() => setConfirmMode("deactivate")}
            >
              Деактивировать
            </button>
          ) : null}
          <button
            type="button"
            className={aa.teamAccessDangerBtn}
            disabled={locked}
            onClick={() => setConfirmMode("delete")}
          >
            Удалить из команды
          </button>
        </div>
      ) : null}
    </div>
  );
}
